import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Activity,
  ArrowLeft,
  CalendarDays,
  Loader2,
  Maximize2,
  Minimize2,
  Moon,
  Pause,
  Play,
  RefreshCw,
  Sun,
  Tv,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { getCurrent, getDaily, getDay, getDays, getSeries } from '@/api/dashboard-live';
import type { DailyPoint, DashboardPayload, SeriesPoint, SnapshotMeta } from '@/api/dashboard-live';
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { PilotageContext } from './context';
import type { Detail, PilotageState } from './context';
import { SERIES } from './theme';
import { ago, dayLabel, duration, time, todayIso } from './format';
import { Explain, WeatherIcon } from './ui';
import { collectDetail } from './details';
import { OverviewScreen } from './screens/OverviewScreen';
import { UsageScreen } from './screens/UsageScreen';
import { MigrationScreen } from './screens/MigrationScreen';
import { InfraScreen } from './screens/InfraScreen';
import { ApisScreen } from './screens/ApisScreen';
import { AlertsScreen } from './screens/AlertsScreen';
import { HistoryScreen } from './screens/HistoryScreen';

const SCREENS = [
  { key: 'ensemble', label: 'Vue d\'ensemble', Component: OverviewScreen },
  { key: 'usage', label: 'Usage', Component: UsageScreen },
  { key: 'migration', label: 'Migration', Component: MigrationScreen },
  { key: 'infra', label: 'Infrastructure', Component: InfraScreen },
  { key: 'api', label: 'API', Component: ApisScreen },
  { key: 'alertes', label: 'Alertes', Component: AlertsScreen },
  { key: 'historique', label: 'Historique', Component: HistoryScreen },
] as const;

/** Séries fines chargées pour tous les écrans (un seul appel, ≤ 60 indicateurs) */
const SERIES_METRICS = [
  'v1.dau', 'v2.dau', 'v1.online_15m', 'v2.online_15m',
  'api.v1.rpm', 'api.v2-api.rpm', 'api.v2-front.rpm', 'api.data.rpm', 'api.adm.rpm',
  'api.v1.p95_ms', 'api.v2-api.p95_ms', 'api.data.p95_ms', 'api.adm.p95_ms', 'api.v2-front.p95_ms',
  'api.v1.err_pct', 'api.v2-api.err_pct', 'api.data.err_pct',
  'probe.v1-app.ms', 'probe.v2-api.ms', 'probe.v2-front.ms', 'probe.data-api.ms', 'probe.adm.ms',
  'infra.app_cpu_pct', 'infra.app_mem_pct', 'infra.cpu_pct_max', 'infra.mem_pct_max',
  'queues.backlog', 'queues.failed', 'db.pg.connections', 'db.pg.tps', 'db.mariadb.threads_connected', 'db.mariadb.qps',
  'alerts.warning', 'alerts.critical',
];

/** Historique journalier chargé pour les écrans (90 jours) */
const DAILY_METRICS = [
  'v1.dau', 'v2.dau', 'v1.dau_loginlog', 'migration.usage_v2_pct', 'migration.schools_v2_only_pct', 'migration.schools_mostly_v2_pct',
  'split.v1_only', 'split.both', 'split.v2_only', 'split.none',
  'v2.new.student', 'v2.new.parent', 'v2.accounts_new_today',
  'v2.activity.attendanceCalls', 'v2.activity.grades', 'v2.activity.messages', 'v2.activity.mails', 'v2.activity.mailsFailed',
  'api.v1.p95_ms_day', 'api.v2-api.p95_ms_day', 'api.data.p95_ms_day', 'api.adm.p95_ms_day', 'api.v2-front.p95_ms_day',
  'api.v1.err_pct_day', 'api.v2-api.err_pct_day', 'api.v1.req_day', 'api.v2-api.req_day',
  'infra.app_cpu_pct_avg_day', 'infra.app_cpu_pct_max_day', 'infra.app_mem_pct_avg_day',
  'alerts.warning', 'alerts.critical',
];

const POLL_MS = 30_000;
const STALE_AFTER_SEC = 15 * 60;
const ROTATE_CHOICES = [20, 30, 60];

function readPref<T>(key: string, fallback: T): T {
  try {
    const v = localStorage.getItem(key);
    return v === null ? fallback : (JSON.parse(v) as T);
  } catch {
    return fallback;
  }
}

function writePref(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // stockage indisponible : préférence non mémorisée
  }
}

/**
 * Tableau de pilotage temps réel, prévu pour un écran plein écran (TV ou
 * poste) : `/pilotage` dans l'ADM, `/pilotage/tv` sans menu.
 *
 * Paramètres d'URL : `ecran` (ensemble, usage, migration, infra, api,
 * alertes, historique) et `jour` (AAAA-MM-JJ : photo de fin de journée).
 * Raccourcis : ← → ou 1-7 (écrans), F (plein écran), espace (rotation), Échap.
 */
export function PilotagePage({ tv = false }: { tv?: boolean }) {
  const [params, setParams] = useSearchParams();
  const screenKey = params.get('ecran') ?? 'ensemble';
  const screenIndex = Math.max(0, SCREENS.findIndex((s) => s.key === screenKey));
  const dayParam = params.get('jour');
  const isLive = !dayParam || dayParam === todayIso();

  const [dark, setDark] = useState<boolean>(() => readPref('pilotage.dark', true));
  const [rotate, setRotate] = useState<boolean>(() => readPref('pilotage.rotate', tv));
  const [rotateSec, setRotateSec] = useState<number>(() => readPref('pilotage.rotateSec', 30));
  const [fullscreen, setFullscreen] = useState(false);

  const [payload, setPayload] = useState<DashboardPayload | null>(null);
  const [snapshot, setSnapshot] = useState<SnapshotMeta | null>(null);
  const [series, setSeries] = useState<Record<string, SeriesPoint[]>>({});
  const [daily, setDaily] = useState<Record<string, DailyPoint[]>>({});
  const [days, setDays] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<Detail | null>(null);
  const [now, setNow] = useState(Date.now());

  const snapshotIdRef = useRef<number | undefined>(undefined);
  const rootRef = useRef<HTMLDivElement>(null);

  const setScreen = useCallback(
    (index: number) => {
      const next = new URLSearchParams(params);
      next.set('ecran', SCREENS[(index + SCREENS.length) % SCREENS.length].key);
      setParams(next, { replace: true });
    },
    [params, setParams]
  );

  const setDayParam = (day: string | null) => {
    const next = new URLSearchParams(params);
    if (day && day !== todayIso()) next.set('jour', day);
    else next.delete('jour');
    setParams(next, { replace: true });
  };

  // Séries et historique, rechargés à chaque nouvelle photo (toutes les 5 min)
  const loadSeriesAndDaily = useCallback(async (day: string | null) => {
    const seriesOpts = day ? { from: `${day}T00:00:00`, to: `${day}T23:59:59` } : { range: '24h' };
    const [s, d] = await Promise.all([getSeries(SERIES_METRICS, seriesOpts), getDaily(DAILY_METRICS, { days: 90, ...(day ? { to: day } : {}) })]);
    setSeries(s);
    setDaily(d);
  }, []);

  // --- mode direct : interrogation toutes les 30 s, 304 tant que rien ne change
  useEffect(() => {
    if (!isLive) return;
    let cancelled = false;
    snapshotIdRef.current = undefined;

    const tick = async () => {
      try {
        const res = await getCurrent(snapshotIdRef.current);
        if (cancelled) return;
        setError(null);
        if (res && res.snapshot && res.payload) {
          snapshotIdRef.current = res.snapshot.id;
          setSnapshot(res.snapshot);
          setPayload(res.payload);
          await loadSeriesAndDaily(null);
        } else if (res && !res.snapshot) {
          setError('Aucune collecte pour le moment : la commande ef:dashboard:collect n\'a pas encore tourné.');
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Erreur de chargement');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    setLoading(true);
    tick();
    const id = window.setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [isLive, loadSeriesAndDaily]);

  // --- mode historique : photo de fin de journée
  useEffect(() => {
    if (isLive || !dayParam) return;
    let cancelled = false;
    setLoading(true);
    getDay(dayParam)
      .then(async (res) => {
        if (cancelled) return;
        if (!res.payload || !res.snapshot) {
          setError(`Aucune photo conservée pour le ${dayLabel(dayParam, true)}.`);
          setPayload(null);
          return;
        }
        setError(null);
        setSnapshot(res.snapshot);
        setPayload(res.payload);
        await loadSeriesAndDaily(dayParam);
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : 'Erreur de chargement'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [isLive, dayParam, loadSeriesAndDaily]);

  useEffect(() => {
    getDays().then(setDays).catch(() => setDays([]));
  }, [snapshot?.day]);

  // horloge
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  // rotation automatique (suspendue pendant la lecture d'un détail)
  useEffect(() => {
    if (!rotate || detail) return;
    const id = window.setInterval(() => setScreen(screenIndex + 1), rotateSec * 1000);
    return () => window.clearInterval(id);
  }, [rotate, rotateSec, detail, screenIndex, setScreen]);

  // plein écran
  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => undefined);
    } else {
      (rootRef.current ?? document.documentElement).requestFullscreen?.().catch(() => undefined);
    }
  }, []);
  useEffect(() => {
    const onChange = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // raccourcis clavier
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (detail || e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement || e.metaKey || e.ctrlKey) return;
      if (e.key === 'ArrowRight') setScreen(screenIndex + 1);
      else if (e.key === 'ArrowLeft') setScreen(screenIndex - 1);
      else if (/^[1-7]$/.test(e.key)) setScreen(Number(e.key) - 1);
      else if (e.key === 'f' || e.key === 'F') toggleFullscreen();
      else if (e.key === ' ') {
        e.preventDefault();
        setRotate((r) => {
          writePref('pilotage.rotate', !r);
          return !r;
        });
      } else return;
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [detail, screenIndex, setScreen, toggleFullscreen]);

  const palette = dark ? SERIES.dark : SERIES.light;
  const state: PilotageState | null = useMemo(
    () =>
      payload && snapshot
        ? { payload, snapshot, series, daily, isLive, day: dayParam ?? todayIso(), palette, dark, now, openDetail: setDetail }
        : null,
    [payload, snapshot, series, daily, isLive, dayParam, palette, dark, now]
  );

  const ageSec = snapshot ? (now - new Date(snapshot.capturedAt).getTime()) / 1000 : 0;
  const stale = isLive && snapshot && ageSec > STALE_AFTER_SEC;
  const Screen = SCREENS[screenIndex].Component;
  const worst = payload ? (['stormy', 'rainy', 'cloudy', 'sunny', 'unknown'] as const).find((st) => Object.values(payload.weather).some((w) => w.state === st)) : undefined;

  return (
    <div
      ref={rootRef}
      className={cn(dark && 'dark', 'pilotage-root', tv || fullscreen ? 'fixed inset-0 z-40' : '-m-4 md:-m-6')}
      style={{ ['--pilotage-surface' as string]: dark ? '#0b1120' : '#f6f7f9' }}
    >
      <div className={cn('flex h-full flex-col gap-3 bg-[#f6f7f9] p-4 text-slate-900 dark:bg-[#0b1120] dark:text-slate-100', (tv || fullscreen) && 'h-screen overflow-hidden')}>
        {/* En-tête */}
        <header className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <div className="flex items-center gap-3">
            {tv && !fullscreen && (
              <Link to="/" className="rounded-md p-1.5 text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-800" title="Retour à l'ADM">
                <ArrowLeft className="h-4 w-4" />
              </Link>
            )}
            {worst ? <WeatherIcon state={worst} className="h-7 w-7" /> : <Activity className="h-7 w-7 text-slate-400" />}
            <div>
              <h1 className="text-lg font-semibold leading-tight">Pilotage École Futée</h1>
              <button
                type="button"
                className="flex items-center gap-1.5 text-xs text-slate-500 hover:underline dark:text-slate-400"
                onClick={() => payload && snapshot && setDetail(collectDetail(payload, snapshot.capturedAt))}
              >
                {isLive ? (
                  <>
                    <span className={cn('h-2 w-2 rounded-full', stale ? 'bg-red-500' : 'animate-pulse bg-emerald-500')} aria-hidden />
                    {snapshot ? `En direct · photo de ${time(snapshot.capturedAt)} (${ago(snapshot.capturedAt, now)})` : 'En direct'}
                  </>
                ) : (
                  <>
                    <CalendarDays className="h-3 w-3" aria-hidden />
                    {snapshot ? `${snapshot.isDayClose ? 'Fin de journée' : 'Dernière photo'} du ${dayLabel(snapshot.day, true)} · ${time(snapshot.capturedAt)}` : dayLabel(dayParam!, true)}
                  </>
                )}
              </button>
            </div>
          </div>

          <nav className="flex flex-1 flex-wrap items-center justify-center gap-1.5" aria-label="Écrans">
            {SCREENS.map((s, i) => (
              <button
                key={s.key}
                type="button"
                onClick={(e) => {
                  setScreen(i);
                  e.currentTarget.blur();
                }}
                className={cn(
                  'rounded-md px-3 py-1.5 text-sm transition-colors',
                  i === screenIndex ? 'bg-slate-900 font-medium text-white dark:bg-white dark:text-slate-900' : 'text-slate-500 hover:bg-slate-200 dark:text-slate-400 dark:hover:bg-slate-800'
                )}
              >
                <span className="mr-1 text-[10px] opacity-50">{i + 1}</span>
                {s.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <span className="hidden text-2xl font-light tabular-nums text-slate-400 lg:inline">{new Date(now).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
            <select
              value={isLive ? '' : dayParam ?? ''}
              onChange={(e) => setDayParam(e.target.value || null)}
              className="h-8 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              style={{ colorScheme: dark ? "dark" : "light", padding: "0 1.75rem 0 0.5rem", borderRadius: 6 }}
              aria-label="Jour affiché"
            >
              <option value="">En direct</option>
              {days
                .filter((d) => d !== todayIso())
                .map((d) => (
                  <option key={d} value={d}>
                    Fin de journée du {dayLabel(d)}
                  </option>
                ))}
            </select>
            <button
              type="button"
              onClick={() => {
                setRotate(!rotate);
                writePref('pilotage.rotate', !rotate);
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                const next = ROTATE_CHOICES[(ROTATE_CHOICES.indexOf(rotateSec) + 1) % ROTATE_CHOICES.length];
                setRotateSec(next);
                writePref('pilotage.rotateSec', next);
              }}
              className={cn('flex h-8 items-center gap-1 rounded-md px-2 text-sm', rotate ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900' : 'text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-800')}
              title={`Rotation automatique des écrans toutes les ${rotateSec} s (clic droit : changer la durée, espace : activer)`}
            >
              {rotate ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              <span className="tabular-nums">{rotateSec} s</span>
            </button>
            <button
              type="button"
              onClick={() => {
                setDark(!dark);
                writePref('pilotage.dark', !dark);
              }}
              className="flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-800"
              title={dark ? 'Thème clair' : 'Thème sombre'}
            >
              {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            {!tv && (
              <Link to="/pilotage/tv" className="flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-800" title="Mode écran TV (sans menu)">
                <Tv className="h-4 w-4" />
              </Link>
            )}
            <button type="button" onClick={toggleFullscreen} className="flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-800" title="Plein écran (F)">
              {fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>
          </div>
        </header>

        {stale && (
          <div className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300">
            Données figées depuis {duration(ageSec)} : la collecte (CronJob cron-adm-dashboard-collect) ne tourne plus.
          </div>
        )}

        {/* Écran */}
        <main className="min-h-0 flex-1 overflow-y-auto" style={tv || fullscreen ? undefined : { height: 'calc(100vh - 10.5rem)', minHeight: 820, flex: 'none' }}>
          {state ? (
            <PilotageContext.Provider value={state}>
              <Screen />
            </PilotageContext.Provider>
          ) : (
            <div className="flex h-full min-h-80 flex-col items-center justify-center gap-3 text-slate-400">
              {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : <RefreshCw className="h-6 w-6" />}
              <p className="text-sm">{loading ? 'Chargement du tableau de pilotage…' : error}</p>
            </div>
          )}
        </main>
        {state && error && <p className="text-xs text-red-500">Dernier rafraîchissement en échec : {error}</p>}

        {/* Panneau de détail */}
        <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
          <SheetContent className={cn('w-full sm:w-[860px] sm:max-w-[92vw]', dark && 'dark')}>
            {detail && state && (
              <PilotageContext.Provider value={state}>
                <SheetHeader>
                  <SheetTitle>{detail.title}</SheetTitle>
                  {detail.subtitle && <SheetDescription>{detail.subtitle}</SheetDescription>}
                </SheetHeader>
                <SheetBody className="space-y-4 dark:text-slate-100">
                  {detail.explain && <Explain>{detail.explain}</Explain>}
                  {detail.body}
                </SheetBody>
              </PilotageContext.Provider>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
}
